import { FullscreenOverlayContainer, OverlayContainer } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { AddToCartService } from '@x/ecommerce-shop/app/core/order/services/add-to-cart.service';
import { ProductListItemService } from '@x/ecommerce-shop/app/core/product/services/product-list-item.service';
import { RegionOverlayService } from '@x/ecommerce-shop/app/core/region/services/region-overlay.service';
import { RegionOpenOverlay } from '@x/ecommerce-shop/app/core/region/state/region.actions';
import { RegionState } from '@x/ecommerce-shop/app/core/region/state/region.state';
import { ShopProductService } from '@x/ecommerce/shop-client';
import { Observable, Subject, first, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'x-region-select',
  templateUrl: './region-select.component.html',
  styleUrls: ['./region-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: OverlayContainer, useClass: FullscreenOverlayContainer }],
  host: { class: 'x-region-select' },
})
export class RegionSelectComponent implements OnDestroy, OnInit {
  private _destroy$ = new Subject<void>();

  @Select(RegionState.openOverlay) openOverlay$: Observable<boolean>;

  @ViewChild('trigger', { static: false, read: ElementRef }) trigger: ElementRef;

  constructor(
    private store: Store,
    private regionOverlayService: RegionOverlayService,
    private route: ActivatedRoute,
    private router: Router,
    private shopProductService: ShopProductService,
    private addToCartService: AddToCartService,
    private productItemViewService: ProductListItemService,
    @Inject(LOCALE_ID) private locale: string,
  ) {}

  ngOnInit() {
    /*
     * Open the region overlay if the regionOverlay query param is present
     * */
    this.route.queryParams
      .pipe(
        first(({ regionOverlay }) => !!regionOverlay), // Only complete when regionOverlay is truthy
        map(async ({ regionOverlay, prodId, quantity, variantId, ...queryParams }) => {
          await this.router.navigate([], { replaceUrl: true, queryParams });

          const channelCode = this.route.snapshot.params.channelCode;
          if (prodId && quantity && variantId && channelCode) {
            const product = await firstValueFrom(
              this.shopProductService.fetchProductItem(Number(prodId), channelCode, this.locale),
            );

            const {
              variantAvailability: { status },
            } = await firstValueFrom(this.productItemViewService.getView$(product));

            this.addToCartService.addProduct(product, {
              variantId: Number(variantId),
              quantity: Number(quantity),
              availabilityStatus: status,
            });

            return;
          }

          this.store.dispatch(new RegionOpenOverlay());
        }),
      )
      .subscribe();
  }

  open() {
    this.store.dispatch(new RegionOpenOverlay());
  }

  handleOutSideClick($event: MouseEvent) {
    this.regionOverlayService.handleOutSideClick($event);
  }

  overlayKeyDown($event: KeyboardEvent) {
    this.regionOverlayService.handleOverlayKeyDown($event);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
